'use client';

import { SearchVilla } from '@/actions/villages';
import { useAutocompleteCities } from '@/components/NewHeader/useAutocompleteCities';
import { useTranslation } from '@/contexts/dictionaryContext';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/lib/utils';
import { pugliaMap, sicilyMap, villagesMap } from '@/utils/maps';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { AiOutlineHome } from 'react-icons/ai';
import {
  IoChevronForward,
  IoInformationCircleOutline,
  IoWarningOutline,
} from 'react-icons/io5';
import { LuLoader2 } from 'react-icons/lu';

import MapPinIcon from '../Icons/MapPin';

type SearchResultsContainerProps = {
  isLoading: boolean;
  hasError: boolean;
  results: SearchVilla[];
  searchValue: string;
  lastSearchedQuery: string | null;
  handleClose: () => void;
  inFlow?: boolean;
};

type PanelProps = {
  children: React.ReactNode;
  inFlow?: boolean;
};

type SectionProps = {
  title: string;
  children: React.ReactNode;
};

type ResultButtonProps = {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  isLoading?: boolean;
  disabled?: boolean;
};

function Panel({ children, inFlow = false }: PanelProps) {
  return (
    <div
      className={cn(
        'min-h-[120px] w-full overflow-hidden max-lg:bg-transparent max-lg:border-0 max-lg:shadow-none lg:bg-white lg:border lg:border-gray-200 lg:rounded-md lg:shadow-lg',
        inFlow ? 'relative mt-2' : 'absolute top-[52px] left-0 z-20',
      )}>
      {children}
    </div>
  );
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="border-b border-gray-100">
      <p className="px-5 pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
        {title}
      </p>
      <div className="max-h-[204px] overflow-y-auto">{children}</div>
    </div>
  );
}

function ResultButton({
  onClick,
  icon,
  title,
  subtitle,
  isLoading = false,
  disabled = false,
}: ResultButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn(
        'w-full px-5 py-3 flex items-center gap-3 text-left',
        disabled || isLoading
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-gray-50',
        isLoading && 'opacity-100 bg-gray-50',
      )}>
      {icon}
      <div className="grow">
        <p className="font-bold">{title}</p>
        <p className="text-sm text-gray-600 font-semibold">{subtitle}</p>
      </div>
      {isLoading ? (
        <LuLoader2 className="animate-spin text-blue w-5 h-5" />
      ) : (
        <IoChevronForward className="text-gray-400" />
      )}
    </button>
  );
}

export function SearchResultsContainer({
  isLoading,
  hasError,
  results,
  searchValue,
  lastSearchedQuery,
  inFlow = false,
  handleClose,
}: SearchResultsContainerProps) {
  const router = useRouter();
  const lang = useLocale();
  const { header, newHeader } = useTranslation();

  const searchTranslations = newHeader.search;
  const allVillasUrl = `${lang === 'en' ? '' : `/${lang}`}/${header.allVillasSlug}`;

  const buildCitySearchUrl = (city: string) => {
    const basePath = `${lang === 'en' ? '' : `/${lang}`}${header.villaCards[0].path}`;
    return `${basePath}?city=${encodeURIComponent(city)}`;
  };

  const buildVillaUrl = (villa: SearchVilla) => {
    const regionCode =
      villa.zone?.region?.code === 'puglia' ? pugliaMap[lang] : sicilyMap[lang];
    return `${lang === 'en' ? '' : `/${lang}`}/${regionCode}/${villagesMap[lang]}/${villa.slug}`;
  };

  const onResultClick = (url: string) => {
    router.push(url);
    handleClose();
  };

  const onCatalogClick = () => {
    handleClose();
  };

  const cityResults = useMemo(() => {
    const uniqueCities = new Set<string>();

    return results
      .filter((villa) => villa.searchMatch?.includes('city'))
      .filter((villa) => {
        if (uniqueCities.has(villa.city)) return false;
        uniqueCities.add(villa.city);
        return true;
      });
  }, [results]);

  const villaResults = useMemo(
    () => results.filter((villa) => villa.searchMatch?.includes('title')),
    [results],
  );

  const {
    autocompleteCities,
    isAutocompleteLoading,
    hasCitiesError,
    loadingPlaceId,
    isCityDetailsLoading,
    selectAutocompleteCity,
  } = useAutocompleteCities({
    searchValue,
    lastSearchedQuery,
    isLoading,
    hasError,
    hasCityResults: cityResults.length > 0,
  });

  const onAutocompleteCityClick = async (
    city: (typeof autocompleteCities)[number],
  ) => {
    const data = await selectAutocompleteCity(city);
    if (!data) return;

    const basePath = `${lang === 'en' ? '' : `/${lang}`}${header.villaCards[0].path}`;
    onResultClick(
      `${basePath}?latitude=${encodeURIComponent(String(data.lat))}&longitude=${encodeURIComponent(String(data.lng))}&city=${encodeURIComponent(data.city)}`,
    );
  };

  const showError = hasError || hasCitiesError;

  const showNoResults =
    !isLoading &&
    !isAutocompleteLoading &&
    !showError &&
    lastSearchedQuery === searchValue &&
    cityResults.length === 0 &&
    autocompleteCities.length === 0 &&
    villaResults.length === 0;

  const showResultsPanel =
    searchValue.length >= 3 &&
    (isLoading ||
      isAutocompleteLoading ||
      showError ||
      cityResults.length > 0 ||
      autocompleteCities.length > 0 ||
      villaResults.length > 0 ||
      showNoResults);

  if (!showResultsPanel) return null;

  return (
    <Panel inFlow={inFlow}>
      {isLoading && (
        <p className="px-5 py-4 text-sm text-gray-500">
          {searchTranslations.loading}
        </p>
      )}

      {!isLoading && showError && (
        <div className="min-h-[120px] px-5 py-4 text-sm text-gray-600 flex flex-col justify-center items-center gap-2">
          <IoWarningOutline className="text-lg w-8 h-8 text-amber-500 mt-[1px]" />
          <p className="font-semibold text-center">
            {searchTranslations.resultsError}
          </p>
        </div>
      )}

      {!isLoading && !showError && cityResults.length > 0 && (
        <Section title={searchTranslations.cityRegionsTitle}>
          {cityResults.map((villa) => (
            <ResultButton
              key={`city-${villa.city}`}
              onClick={() => onResultClick(buildCitySearchUrl(villa.city))}
              icon={<MapPinIcon className="fill-current text-blue w-8 h-8" />}
              title={villa.city}
              subtitle={
                villa.zone?.region?.code === 'puglia'
                  ? searchTranslations.regions.puglia
                  : searchTranslations.regions.sicily
              }
              disabled={isCityDetailsLoading}
            />
          ))}
        </Section>
      )}

      {!isLoading &&
        !showError &&
        cityResults.length === 0 &&
        autocompleteCities.length > 0 && (
          <Section title={searchTranslations.cityRegionsTitle}>
            {autocompleteCities.map((city) => (
              <ResultButton
                key={`autocomplete-city-${city.placeId}`}
                onClick={() => onAutocompleteCityClick(city)}
                icon={<MapPinIcon className="fill-current text-blue w-8 h-8" />}
                title={city.city}
                subtitle={city.region}
                isLoading={loadingPlaceId === city.placeId}
                disabled={isCityDetailsLoading}
              />
            ))}
          </Section>
        )}

      {!isLoading && !showError && villaResults.length > 0 && (
        <Section title={searchTranslations.villasTitle}>
          {villaResults.map((villa) => (
            <ResultButton
              key={`villa-${villa.id}`}
              onClick={() => onResultClick(buildVillaUrl(villa))}
              icon={<AiOutlineHome className="w-8 h-8 text-blue" />}
              title={villa.title}
              subtitle={villa.city}
              disabled={isCityDetailsLoading}
            />
          ))}
        </Section>
      )}

      {!isLoading && showNoResults && (
        <div className="min-h-[120px] px-5 py-4 text-sm text-gray-600 flex flex-col justify-center items-center">
          <p className="font-semibold text-center">
            {searchTranslations.noResults}
          </p>
          <Link
            href={allVillasUrl}
            onClick={onCatalogClick}
            className="mt-2 inline-block font-semibold text-blue hover:underline">
            {searchTranslations.visitCatalog}
          </Link>
        </div>
      )}

      {!isLoading &&
        !showError &&
        (cityResults.length > 0 ||
          autocompleteCities.length > 0 ||
          villaResults.length > 0) && (
          <div className="m-4 p-3 border border-blue-100 bg-blue-50 rounded-md text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <IoInformationCircleOutline className="text-lg text-blue-600 mt-[1px]" />
              <div>
                <p className="font-semibold">
                  {searchTranslations.helperTitle}
                </p>
                <p>{searchTranslations.helperChooseCity}</p>
                <p>{searchTranslations.helperChooseVilla}</p>
              </div>
            </div>
          </div>
        )}
    </Panel>
  );
}
